<template>
	<div>
		<Modal
			:is-open="isOpen"
			:title="`Login as ${user.name}`"
			@close="closeModal"
		>
			<template #body>
				<div class="login-content">
					<div class="user-icon">👤</div>
					<h3>Confirm Admin Access</h3>

					<div class="user-details">
						<h4>Account Details</h4>
						<div class="detail-row">
							<strong>Name:</strong> {{ user.name }}
						</div>
						<div class="detail-row">
							<strong>Email:</strong> {{ user.email }}
						</div>
					</div>

					<div class="security-notice">
						<p>⚠️ This action will be logged for security purposes</p>
					</div>

					<form class="login-as-form" @submit.prevent="performLogin">
						<label for="admin-password">Your Admin Password</label>
						<input
							id="admin-password"
							v-model="password"
							type="password"
							placeholder="Enter your password to continue"
							class="form-input"
							:disabled="loading"
							required
							autocomplete="current-password"
							@keyup.enter="performLogin"
						>
					</form>

					<div v-if="errorMessage" class="message error">
						{{ errorMessage }}
					</div>
					<div v-else-if="successMessage" class="message success">
						{{ successMessage }}
					</div>
				</div>
			</template>

			<template #actions>
				<button @click="closeModal" :disabled="loading">
					Cancel
				</button>
				<button @click="performLogin" :disabled="loading || !password">
					{{ loading ? 'Authenticating...' : 'Confirm Login' }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<script>
import Modal from './Modal.vue';

export default {
	name: 'login-as-user-modal',
	components: {
		Modal
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		userId: {
			type: String,
			default: ''
		}
	},
	store: ['users'],
	computed: {
		user() {
			return this.users[this.userId] || {
				user_uuid: null
			}
		}
	},

	data() {
		return {
			password: '',
			loading: false,
			errorMessage: null,
			successMessage: null
		}
	},

	methods: {
		closeModal() {
			if (!this.loading) {
				this.$emit('close');
				this.resetForm();
			}
		},

		resetForm() {
			this.password = '';
			this.errorMessage = null;
			this.successMessage = null;
			this.loading = false;
		},

		performLogin() {
			if (!this.password || this.loading) return;

			this.loading = true;
			this.errorMessage = null;
			this.successMessage = null;

			this.$http.post('/admin/user/login-as', {
				password: this.password,
				userUuid: this.user.user_uuid
			})
				.then(() => {
					this.loading = false;
					this.successMessage = 'Authentication successful! Check your email for a login link.';

					// Auto close after success
					setTimeout(() => {
						this.closeModal();
						this.$emit('close');
					}, 2000);
				})
				.catch((error) => {
					this.loading = false;

					if (error.response?.status === 401) {
						this.errorMessage = 'Invalid password. Please try again.';
					} else if (error.response?.status === 403) {
						this.errorMessage = 'You do not have permission to login as this user.';
					} else if (error.response?.data?.message) {
						this.errorMessage = error.response.data.message;
					} else {
						this.errorMessage = 'An error occurred. Please try again.';
					}

					console.error('Login as user failed:', error);
				});
		}
	}
}
</script>

<style scoped>
	.login-content {
		padding: 20px 0;
	}

	.user-icon {
		font-size: 48px;
		margin-bottom: 16px;
		text-align: center;
	}

	.login-content h3 {
		margin: 0 0 20px 0;
		font-size: 20px;
		color: #333;
		text-align: center;
	}

	.user-details {
		background: #f8f9fa;
		padding: 16px;
		border-radius: 8px;
		margin-bottom: 16px;
	}

	.user-details h4 {
		margin: 0 0 12px 0;
		font-size: 16px;
		color: #495057;
	}

	.detail-row {
		margin-bottom: 8px;
		color: #666;
	}

	.detail-row strong {
		color: #333;
		margin-right: 8px;
	}

	.security-notice {
		background: #fff3cd;
		border: 1px solid #ffeeba;
		color: #856404;
		padding: 12px;
		border-radius: 4px;
		margin-bottom: 20px;
	}

	.security-notice p {
		margin: 0;
		font-size: 14px;
		text-align: center;
	}

	.login-as-form {
		margin-bottom: 20px;
	}

	.login-as-form label {
		display: block;
		margin-bottom: 8px;
		font-weight: 500;
		color: #333;
	}

	.login-as-form .form-input {
		width: 100%;
		padding: 10px;
		border: 1px solid #ced4da;
		border-radius: 4px;
		font-size: 14px;
		transition: border-color 0.2s;
	}

	.form-input:focus {
		outline: none;
		border-color: #80bdff;
		box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
	}

	.form-input:disabled {
		background: #e9ecef;
		cursor: not-allowed;
	}

	.message {
		padding: 12px;
		border-radius: 4px;
		text-align: center;
		animation: fadeIn 0.3s ease-in;
	}

	.message.success {
		background: #d4edda;
		color: #155724;
		border: 1px solid #c3e6cb;
	}

	.message.error {
		background: #f8d7da;
		color: #721c24;
		border: 1px solid #f5c6cb;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(-5px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
</style>
